import { useMemo } from 'react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';

const ENABLED_CF_STATES = [State.ACTIVE, State.PROMOTE];

const useLocationCustomizationFlags = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const { getCustomizationFlag } = useAppFlagStore();
  const { isFlagStoreLoaded } = useScopedAppFlagStore();

  return useMemo(() => {
    const hasEnabledFlag = (featureEnum: Feature, locationId: string) => {
      const [flag] = getCustomizationFlag({ locationIds: [locationId], featureEnum });
      return !!flag?.state && ENABLED_CF_STATES.includes(flag.state);
    };

    const weaveAirCfEnabledLocationIds = selectedLocationIds.filter((locationId) =>
      [Feature.WEAVE_MESSAGING_AIR, Feature.WEAVE_VOICE_AIR].some((featureEnum) =>
        hasEnabledFlag(featureEnum, locationId)
      )
    );

    const onlineSchedulingEnabledLocationIds = selectedLocationIds.filter((locationId) =>
      hasEnabledFlag(Feature.ONLINE_SCHEDULING, locationId)
    );

    return {
      weaveAirCfEnabledLocationIds,
      onlineSchedulingEnabledLocationIds,
      isLoading: !isFlagStoreLoaded,
    };
  }, [getCustomizationFlag, isFlagStoreLoaded, selectedLocationIds]);
};

const usePreferenceRequestAirAccess = () => {
  const { weaveAirCfEnabledLocationIds, isLoading } = useLocationCustomizationFlags();

  return {
    airEnabledLocationIds: weaveAirCfEnabledLocationIds,
    isLoadingAirEnabledLocationIds: isLoading,
  };
};

// custom hook to check access for schedule request features
export const useScheduleRequestFeaturesAccess = () => {
  const { airEnabledLocationIds, isLoadingAirEnabledLocationIds } = usePreferenceRequestAirAccess();
  const { onlineSchedulingEnabledLocationIds, isLoading: isLoadingCustomizationFlags } =
    useLocationCustomizationFlags();

  const enabledLocationIds = Array.from(new Set([...onlineSchedulingEnabledLocationIds, ...airEnabledLocationIds]));

  const hasAccess = enabledLocationIds.length > 0;
  const isLoading = isLoadingAirEnabledLocationIds || isLoadingCustomizationFlags;

  return {
    isLoading,
    hasScheduleRequestFeaturesAccess: !isLoading && hasAccess,
    scheduleRequestFeatureEnabledLocationIds: enabledLocationIds,
    hasAirScheduleRequestAccess: airEnabledLocationIds.length > 0,
  };
};
